import React, { forwardRef, useRef } from 'react';
import { useUID } from 'react-uid';
import { ReactComponent as ClearIcon } from 'assets/icons/icon-close.svg';
import { motion } from 'framer-motion';

function composeRefs<T>(refs: React.Ref<T>[]): React.Ref<T> {
  return function(component: T) {
    refs.forEach((ref: any) => {
      if (typeof ref === 'function') {
        ref(component);
      } else if (ref && 'current' in ref) {
        ref.current = component;
      }
    });
  };
}

const Input = forwardRef(
  (
    {
      type = 'text',
      label = '',
      inputClasses = '',
      wrapperClasses = '',
      icon = null,
      invalid = false,
      shouldValidate = false,
      touched = false,
      onChange = () => {},
      onResetClick,
      error = '',
      value = '',
      ...inputProps
    }: any,
    externalRef,
  ) => {
    const internalRef = useRef<HTMLInputElement>();
    const uid = useUID();
    const isErrored = (invalid && shouldValidate && touched) || Boolean(error);
    const tailwindClasses = {
      inputWrapper: ['input-wrapper', wrapperClasses],
      input: ['pr-8', inputClasses],
    };

    if (inputProps.disabled) tailwindClasses.inputWrapper.push('disabled');
    if (isErrored) tailwindClasses.inputWrapper.push('errored');
    if (icon) tailwindClasses.input.push('pl-10');

    const inputFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        (event.keyCode <= 46 && event.keyCode != 32) ||
        event.ctrlKey ||
        event.metaKey
      )
        return; // allow non-char keys (other than space)
      if (event.key.match(/[^\d.\-]/g)) event.preventDefault(); // numbers and decimals only
      if (event.key == '.' && event.currentTarget.value.match(/.*\..*/g))
        event.preventDefault(); // decimal can't exist more than once
      if (event.key == '-' && event.currentTarget.selectionStart > 0)
        event.preventDefault(); // only allow minus symbol as the first char
    };

    return (
      <div className={tailwindClasses.inputWrapper.join(' ')}>
        <label htmlFor={uid}>{label}</label>

        <div className="relative flex items-center">
          {icon}

          {typeof onResetClick === 'function' && value?.length > 0 && (
            <div
              className="absolute right-0 flex items-center"
              onClick={() => {
                onResetClick();
                internalRef.current.focus();
              }}
            >
              <ClearIcon className="w-8 h-full my-auto mr-1 text-gray-600 bg-transparent cursor-pointer hover:text-gray-900" />
            </div>
          )}

          <input
            ref={composeRefs([internalRef, externalRef])}
            type={type === 'number' ? 'text' : type}
            id={uid}
            value={value || ''}
            onChange={onChange}
            onKeyDown={type === 'number' ? (e) => inputFilter(e) : undefined}
            inputMode={type === 'number' ? 'numeric' : undefined}
            placeholder={type === 'number' ? '0.00' : undefined}
            className={tailwindClasses.input.join(' ')}
            {...inputProps}
          />
        </div>
        {error && (
          <p className="mt-1 text-sm font-semibold tracking-tight text-red-800">
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default Input;
